import { useMemo } from "react";
import type { ConnectProto } from "@racoon/api";
import { Namespace, useDateFnsLocale, useT, type TranslationKey } from "@racoon/i18n";
import { EMPTY_CELL, fromTimestamp } from "@racoon/lib";
import { Hyperlink, type CellContext, type Column } from "@racoon/ui";
import { PhotoReportModal } from "~/features/photo-report-modal/ui/photo-report.modal";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { plannedContainerCount } from "~/shared/lib/job-containers";
import {
  formatVolume,
  getJobFactContainerCount,
  getJobFactVolume,
  getJobTariff,
  getJobVolume,
} from "~/shared/lib/job-metrics";
import { fullName } from "~/shared/helpers";
import { JobIncident } from "../components/job-map/job-incident";
import { JobStatusPill } from "../components/job-status-pill";
import type { OrderRow } from "./adapters";
import { formatMapDay, formatMapTime, formatMapWindow } from "./job-map-format";
import { TwoLines } from "../components/job-map/two-lines";

type Props = {
  /** Opens the job in the edit dialog. */
  onJobIdClick?: (row: OrderRow) => void;
  onRouteClick?: (routeId: bigint) => void;
  /** The listed jobs' incidents; undefined until they arrive. */
  incidents?: readonly ConnectProto.DriverIncident[];
};

/**
 * The columns of an order's jobs table (`OrderJobsTable`), one row per attempt. Widths
 * are each column's minimum, so the table fits a 1440px screen whole; wider screens
 * stretch them all alike. At 1440 an incident or a driver's name may take two lines;
 * from 1920 they fit on one.
 */
export const useOrderJobsColumns = ({ onJobIdClick, onRouteClick, incidents }: Props) => {
  const t = useT();
  const locale = useDateFnsLocale();
  // The trip is fetched as the active company's: another provider's would not load
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");

  return useMemo<Column<OrderRow>[]>(() => {
    const day = (date: Date) => formatMapDay(date, locale);
    const time = (date: Date) => formatMapTime(date, locale);
    const header = (key: TranslationKey<Namespace.Orders>) =>
      t(Namespace.Orders, key).toUpperCase();
    const volume = (value: number | undefined) =>
      t(Namespace.Orders, "jobMap.volume", { volume: formatVolume(value) });

    return [
      {
        id: "attempt",
        header: header("jobMap.jobs.attempt"),
        accessor: (r) => r._attempt,
        width: 28,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, number>) => (
          <span className="text-text-information">#{info.getValue()}</span>
        ),
      },
      {
        id: "jobId",
        header: header("jobMap.jobs.id"),
        accessor: (r) => r.number,
        width: 78,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, string>) =>
          onJobIdClick ? (
            <Hyperlink onOpen={() => onJobIdClick(info.row.original)} data-testid="orders-job-link">
              {info.getValue()}
            </Hyperlink>
          ) : (
            info.getValue()
          ),
      },
      {
        id: "jobStatus",
        header: header("jobMap.jobs.status"),
        accessor: (r) => r.status,
        width: 108,
        // 100px in the design: every status pill in the table the same width
        cell: (info: CellContext<OrderRow, string>) => (
          <JobStatusPill status={info.getValue()} className="w-[100px]" />
        ),
      },
      {
        id: "incident",
        header: header("jobMap.jobs.incident"),
        width: 105,
        cell: (info: CellContext<OrderRow, unknown>) => (
          <JobIncident job={info.row.original.rawJob} incidents={incidents} />
        ),
      },
      {
        id: "route",
        header: header("jobMap.jobs.route"),
        accessor: (r) => r.route,
        width: 72,
        cell: (info: CellContext<OrderRow, string | null>) => {
          const { routeId, serviceProviderId, serviceReceiverId } = info.row.original.rawJob;
          if (
            !routeId ||
            !onRouteClick ||
            isOwnForWrite(serviceProviderId, serviceReceiverId) !== true
          )
            return info.getValue() ?? EMPTY_CELL;
          return <Hyperlink onOpen={() => onRouteClick(routeId)}>{info.getValue()}</Hyperlink>;
        },
      },
      {
        id: "plannedFor",
        header: header("jobMap.jobs.plannedFor"),
        width: 142,
        cell: (info: CellContext<OrderRow, unknown>) => {
          const start = fromTimestamp(info.row.original.rawJob.twStartAt);
          const end = fromTimestamp(info.row.original.rawJob.twEndAt);
          if (!start) return null;
          return <TwoLines first={formatMapWindow(start, end, locale)} second={day(start)} />;
        },
      },
      {
        id: "reportedAt",
        header: header("jobMap.jobs.reportedAt"),
        width: 101,
        cell: (info: CellContext<OrderRow, unknown>) => {
          const reportedAt = fromTimestamp(info.row.original.rawJob.factEndAt);
          return reportedAt ? <TwoLines first={time(reportedAt)} second={day(reportedAt)} /> : null;
        },
      },
      {
        id: "photo",
        header: header("jobMap.jobs.photo"),
        width: 44,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, unknown>) => (
          <PhotoReportModal
            job={info.row.original.rawJob}
            getJobs={() => info.table.getRowModel().rows.map((row) => row.original.rawJob)}
          />
        ),
      },
      {
        id: "plannedContainers",
        header: header("jobMap.jobs.plannedContainers"),
        accessor: (r) => plannedContainerCount(r.rawJob) || EMPTY_CELL,
        width: 96,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, number | string>) => (
          <span className="text-slate-400">{info.getValue()}</span>
        ),
      },
      {
        id: "plannedVolume",
        header: header("jobMap.jobs.plannedVolume"),
        accessor: (r) => volume(getJobVolume(r.rawJob) || undefined),
        width: 92,
        align: "center" as const,
        cell: (info: CellContext<OrderRow, string>) => (
          <span className="text-slate-400">{info.getValue()}</span>
        ),
      },
      {
        id: "factContainers",
        header: header("jobMap.jobs.factContainers"),
        accessor: (r) => getJobFactContainerCount(r.rawJob) ?? "-",
        width: 88,
        align: "center" as const,
      },
      {
        id: "factVolume",
        header: header("jobMap.jobs.factVolume"),
        accessor: (r) => volume(getJobFactVolume(r.rawJob)),
        width: 91,
        align: "center" as const,
      },
      {
        id: "cost",
        header: header("jobMap.jobs.cost"),
        accessor: (r) => {
          const cost = getJobTariff(r.rawJob);
          return cost ? t(Namespace.Orders, "jobMap.cost", { cost }) : "-";
        },
        width: 88,
      },
      {
        id: "plate",
        header: header("jobMap.jobs.plate"),
        accessor: (r) => r.rawJob.driverOnShift?.truck?.plateNumber || "-",
        width: 81,
      },
      {
        id: "driver",
        header: header("jobMap.jobs.driver"),
        accessor: (r) => fullName(r.rawJob.driverOnShift?.driver) || "-",
        width: 80,
      },
    ];
  }, [t, locale, isOwnForWrite, onJobIdClick, onRouteClick, incidents]);
};
